import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { COLORS } from 'src/styles/colors';
import { CareTeamTemplateService } from '../../../../services/careTeamTemplate.service';
import {
  ICareTeamTemplate,
  ICareTeamTemplateListItem,
} from '../../../../interfaces/ICareTeamTemplate';
import { ICareTeamLookupDropdown } from '../../../../interfaces/ICareTeamTemplateDropdownData';
import { ISearchCareTeamTemplate } from '../../../../interfaces/ISearchCareTeamTemplate';
import { CareTeamTemplateFormComponent } from './care-team-template-form/care-team-template-form.component';

@Component({
  selector: 'app-care-team-management',
  templateUrl: './care-team-management.component.html',
  styleUrls: ['./care-team-management.component.css'],
  standalone: false,
})
export class CareTeamManagementComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild('templateForm') templateForm?: CareTeamTemplateFormComponent;

  public data: MatTableDataSource<ICareTeamTemplateListItem> =
    new MatTableDataSource<ICareTeamTemplateListItem>([]);

  public displayedColumns: string[] = [
    'name',
    'category',
    'note',
    'members',
    'cttactions',
  ];

  public categoryOptions: ICareTeamLookupDropdown[] = [];

  public name: string = '';
  public selectedCategoryId?: number;

  public totalRecords: number = 0;

  public isLoading: boolean = false;

  public showForm: boolean = false;
  public editingTemplateId?: number;

  public isViewModalVisible: boolean = false;
  public viewLoading: boolean = false;
  public viewingTemplate: ICareTeamTemplate | null = null;

  constructor(
    private readonly careTeamTemplateService: CareTeamTemplateService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit() {
    this.careTeamTemplateService.getDropdownData().subscribe({
      next: (res) => {
        this.categoryOptions = res?.data?.categories || [];
      },
      error: (err) => {
        console.error('Error fetching care team categories:', err);
      },
    });
  }

  ngAfterViewInit() {
    this.pageChange();
  }

  pageChange() {
    const searchParams: ISearchCareTeamTemplate = {};

    const trimmedName = this.name?.trim();
    if (trimmedName) {
      searchParams.name = trimmedName;
    }
    if (this.selectedCategoryId) {
      searchParams.careTeamCategoryIds = [this.selectedCategoryId];
    }

    this.isLoading = true;
    this.careTeamTemplateService
      .getCareTeamTemplates(
        this.paginator.pageIndex + 1,
        this.paginator.pageSize,
        searchParams
      )
      .subscribe({
        next: (res) => {
          this.data.data = Array.isArray(res?.data) ? res.data : [];
          this.totalRecords = res.totalRecords ?? 0;
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.error(err);
        },
      });
  }

  searchTemplatesForm() {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.pageChange();
  }

  deleteTemplate(template: ICareTeamTemplateListItem) {
    Swal.fire({
      title: `${this.translate.instant('Delete Template?')}`,
      text: `${this.translate.instant(
        'This care team template will be permanently deleted and cannot be undone.'
      )}`,
      icon: 'warning',
      iconColor: '#d33',
      showCancelButton: true,
      confirmButtonColor: COLORS.green1,
      cancelButtonColor: '#d33',
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Delete')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.careTeamTemplateService
          .deleteCareTeamTemplate(template.id)
          .subscribe({
            next: () => {
              Swal.fire(
                `${this.translate.instant('Deleted')}!`,
                `${this.translate.instant(
                  'The care team template has been deleted.'
                )}`,
                'success'
              );
              this.pageChange();
            },
            error: (err) => {
              Swal.fire({
                title: `${this.translate.instant('Error')}!`,
                text: `${this.translate.instant(err.error?.message)}`,
                icon: 'error',
                iconColor: '#d33',
                confirmButtonColor: COLORS.green1,
                confirmButtonText: `Ok`,
              });
              console.error('Error deleting care team template:', err);
            },
          });
      }
    });
  }

  onEdit(template: ICareTeamTemplateListItem) {
    this.editingTemplateId = template.id;
    this.showForm = true;
  }

  hasUnsavedChanges(): boolean {
    return this.showForm && !!this.templateForm?.hasUnsavedChanges();
  }

  onView(template: ICareTeamTemplateListItem) {
    this.isViewModalVisible = true;
    this.viewLoading = true;
    this.viewingTemplate = null;

    this.careTeamTemplateService.getCareTeamTemplateById(template.id).subscribe({
      next: (res) => {
        this.viewingTemplate = res.data;
        this.viewLoading = false;
      },
      error: (err) => {
        this.viewLoading = false;
        console.error('Error loading care team template:', err);
      },
    });
  }

  closeViewModal() {
    this.isViewModalVisible = false;
    this.viewingTemplate = null;
  }

  getInitials(displayName: string): string {
    if (!displayName) {
      return '';
    }

    const parts = displayName.trim().split(/\s+/);
    const initials =
      parts.length > 1
        ? parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
        : parts[0].charAt(0);

    return initials.toUpperCase();
  }

  createTemplate() {
    this.editingTemplateId = undefined;
    this.showForm = true;
  }

  onFormClosed() {
    this.showForm = false;
    this.editingTemplateId = undefined;
  }

  onFormSaved() {
    this.showForm = false;
    this.editingTemplateId = undefined;
    setTimeout(() => this.pageChange());
  }
}